import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import API from "../../api";
import toastr from "toastr";

interface Department {
  id: number;
  dept_name: string;
}

interface TestCategory {
  id?: number;
  dept_id: number;
  type: string;
  category_name: string;
  description: string;
  amount: string | number;
}

interface Props {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  categoryData: TestCategory | null;
  departments: Department[];
  onSuccess: () => void;
}

const TestCategoryModal: React.FC<Props> = ({
  show,
  onClose,
  mode,
  categoryData,
  departments,
  onSuccess,
}) => {
  const [deptId, setDeptId] = useState<number | "">("");
  const [type, setType] = useState("single");
  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && categoryData) {
      setDeptId(categoryData.dept_id || "");
      setType(categoryData.type || "single");
      setCategoryName(categoryData.category_name || "");
      setDescription(categoryData.description || "");
      setAmount(categoryData.amount ? String(categoryData.amount) : "");
    } else {
      // Auto-select first department in add mode
      setDeptId(departments.length > 0 ? departments[0].id : "");
      setType("single");
      setCategoryName("");
      setDescription("");
      setAmount("");
    }
  }, [mode, categoryData, show, departments]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toastr.error("Category name is required");
      return;
    }
    if (deptId === "") {
      toastr.error("Please select a department");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        dept_id: deptId,
        type,
        category_name: categoryName.trim(),
        description: description.trim() || null,
        amount: amount ? parseFloat(amount) : 0.0,
      };

      if (mode === "add") {
        await API.post("/testcategories/createCategory", payload);
        toastr.success("Test category created successfully");
      } else {
        await API.put(`/testcategories/updateCategory/${categoryData?.id}`, payload);
        toastr.success("Test category updated successfully");
      }
      onSuccess();
    } catch (err: any) {
      const msg = err.response?.data?.message || "Operation failed";
      toastr.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>
          {mode === "add" ? "Create New Test Category" : "Edit Test Category"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="bg-dark text-white">

      
          <Form.Group className="mb-3">
            <Form.Label>Department</Form.Label>
            <select
              value={deptId}
              onChange={(e) => setDeptId(e.target.value ? Number(e.target.value) : "")}
              className="form-control bg-dark text-white border-secondary"
              required
            >
              <option value="">-- Select Department --</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.dept_name}
                </option>
              ))}
            </select>
          </Form.Group>

         
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="form-control bg-dark text-white border-secondary"
            >
              <option value="single">Single</option>
              <option value="group">Group</option>
            </select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category Name</Form.Label>
            <Form.Control
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Enter category name"
              required
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

        
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

       
          <Form.Group>
            <Form.Label>Amount (₹)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="bg-dark text-white border-secondary"
            />
          </Form.Group>

        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TestCategoryModal;